import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constant";
import { StateContext } from "../../context/globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { ScreenTypes } from "../../enums";
import BrandedRasterIcon from "../../component/reusableComponent/brandedRasterIcon/BrandedRasterIcon";
import styles from "./paymentFailure.module.scss";
import { failedLogo } from "../../constant";
import Typography from "../../component/reusableComponent/typography/Typography";
import failureCheck from "../../assets/failureCheck.png";
import AppButton from "../../component/reusableComponent/button/button";
import BookingNavBar from "../../component/reusableComponent/bookingNavBar/bookingNavBar";
import {
  BOOKING_RETRY_LIMIT as RETRY_LIMIT,
  INITIAL_BREADCRUMBS_LIST,
} from "../../constant/bookingInProgressConstant";
import { flightOrHotelSelect } from "../../utils/booking/breadCrumb/breadCrumbUtils";
import { isEmbedMode } from "../../utils/embedMode";
import { MOBILE_MEDIA_QUERY } from "../../constant/breakpoints";

export default function PaymentFailure() {
  const navigate = useNavigate();

  const state = useContext(StateContext);
  const {
    setFirstScreen,
    setScreen,
    bookedtransIds,
    setBookedTransIds,
    payDetails,
    setActiveBreadCrumbs,
    selectedFlightOptions,
    setBreadList,
    stripePaymentFailureMessage,
    setStripePaymentFailureMessage,
  }: iGlobalContext = state;

  // Snapshot the error message + attempt count once on mount. Context resets
  // on a full page refresh, so fall back to the sessionStorage twin written
  // right before the navigate here (reviewStepsUtils.handleCompletePurchase /
  // ActivityPayment.handleSubmit). If neither source has a value we treat
  // this as a direct/cold entry (no error) and hide the retry UI.
  const [errorMessage] = useState<string>(() => {
    if (stripePaymentFailureMessage && stripePaymentFailureMessage.trim()) {
      return stripePaymentFailureMessage;
    }
    try {
      return sessionStorage.getItem("_zenvoya_stripe_error_message") || "";
    } catch {
      return "";
    }
  });
  const [attempts] = useState<number>(() => {
    try {
      return parseInt(sessionStorage.getItem("_zenvoya_booking_retry_count") || "0", 10) || 0;
    } catch {
      return 0;
    }
  });
  // The mount effect below clears bookedtransIds, but for flight/stay Try Again
  // re-confirms the SAME PaymentIntent (payDetails.secretKey survives, no new
  // prebook) and /paymentsuccess confirms only from bookedtransIds. Without this snapshot a
  // retry after a decline charged the card, skipped the confirm and landed on
  // the flights listing (prod 6AH-TLM-WY1). Read on the first render, before
  // the clear runs.
  const [retryBookedTransIds] = useState<any>(() => bookedtransIds);

  useEffect(() => {
    setBookedTransIds(null);
    setActiveBreadCrumbs(INITIAL_BREADCRUMBS_LIST[0]);
  }, [setBookedTransIds]);

  // Browser Back on /paymentfailure should land the user on the Traveller Form
  // (data preserved via `_traveler_info_array` localStorage), and browser
  // Forward must NOT be able to return to /paymentfailure. We can't cancel a
  // popstate, but we can react to it: set a sessionStorage flag that
  // BookingInProgress reads on mount to roll the wizard to the traveler step,
  // then `navigate(...)` (push) to /booking. The push clears the forward stack
  // — exactly what makes /paymentfailure unreachable from Forward. Runs at
  // most once per Back press (no re-arm loop), so Safari's replaceState 100/10s
  // throttle never applies.
  useEffect(() => {
    function onPopState() {
      try {
        sessionStorage.setItem("_zenvoya_booking_return_target", "traveler");
      } catch {}
      navigate(ROUTES.BookingInProgress);
    }
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [navigate]);

  // The user is leaving the failure page in a way that ends this retry loop
  // (See other options, logo click). Drop the counter + persisted error so a
  // brand-new booking session never inherits either value.
  function clearRetryState() {
    try {
      sessionStorage.removeItem("_zenvoya_booking_retry_count");
      sessionStorage.removeItem("_zenvoya_stripe_error_message");
    } catch {}
    setStripePaymentFailureMessage("");
  }

  function handleTryAgain() {
    // Land the user DIRECTLY on the Payment substep (not the traveler form) —
    // BookingInProgress reads this flag on mount and rolls the wizard to
    // include the payment step. clientSecret and paymentIntent from the
    // successful prebook survive in state, so the Stripe form reloads intact.
    // For Activity, ActivityReview restores its own REVIEW_STORAGE_KEY
    // snapshot ("payment" substep) so the user lands on the Stripe form there
    // too. Traveler info is preserved either way (localStorage).
    // Put back the ids this PaymentIntent was prebooked with, so a successful
    // retry can confirm. Only when they belong to the prebook still held in
    // payDetails (same itinerary) and carry a transaction id, which
    // useConfirmBookingAPi needs to confirm. Activity re-prebooks on remount
    // and never sets payDetails, so it is skipped here.
    const ids = retryBookedTransIds;
    if (
      ids?.itineraryID &&
      ids.itineraryID === payDetails?.itineraryID &&
      (ids.flightTransactionId || ids.hotelTransactionId || ids.activityTransactionId)
    ) {
      setBookedTransIds(ids);
    }
    try {
      sessionStorage.setItem("_zenvoya_booking_return_target", "payment");
    } catch {}
    navigate(ROUTES.BookingInProgress);
  }

  function handleSeeOtherOptions() {
    clearRetryState();
    flightOrHotelSelect(selectedFlightOptions, setBreadList, setActiveBreadCrumbs);
    navigate(ROUTES.BookingInProgress);
  }

  const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;

  // Try Again only while we're inside the retry window AND we have a real
  // failure to retry against. attempts === 0 means someone landed here by
  // direct URL — fall back to the original "See other options" copy.
  const canRetry = attempts >= 1 && attempts <= RETRY_LIMIT;

  // "N attempts left" text is shown across THREE states for RETRY_LIMIT=2:
  //   attempts=1 → "2 attempts left" (Try Again)
  //   attempts=2 → "1 attempt left"  (Try Again)
  //   attempts=3 → "0 attempts left" (See other options — retries exhausted)
  // Retry gate `canRetry` covers only the first two (attempts 1, 2); the "0
  // attempts left" state deliberately renders alongside "See other options"
  // so the user gets the countdown message on the exhaustion screen too.
  // attempts=0 (direct nav) and attempts>=RETRY_LIMIT+2 hide the text entirely.
  const showAttemptsLeft = attempts >= 1 && attempts <= RETRY_LIMIT + 1;
  const attemptsLeftValue = Math.max(0, RETRY_LIMIT + 1 - attempts);

  return (
    <>
      {!isMobile && !isEmbedMode() && (
        <BookingNavBar
          onLogoClick={() => {
            clearRetryState();
            navigate(ROUTES.Home);
            setScreen("");
            setFirstScreen(ScreenTypes.NewTrip);
          }}
        />
      )}
      <div className={styles.mainContainer}>
        <div className={styles.failureContainer}>
          <div className={styles.mainContainer}>
            <BrandedRasterIcon src={failedLogo} alt="booked" className={styles.illustration} />

            <div className={styles.header}>
              <img src={failureCheck} alt="Booked" className={styles.checkIcon} />
              <Typography size="size16" weight={600}>
                Booked failed
              </Typography>
            </div>

            {errorMessage && (
              <Typography size="size14" weight={500} className={styles.errorMessage}>
                {errorMessage}
              </Typography>
            )}

            <div className={styles.description}>
              <Typography size="size14">
                Your booking has failed.
                <div>
                  <Typography size="size14" className={styles.subText}>
                    {canRetry
                      ? "You can try again with the same card or use another card."
                      : "However, we have other options for you! Please check them below."}
                  </Typography>
                </div>
              </Typography>
              <Typography size="size14" className={styles.confirmationText}>
                *Your traveler info is saved for faster check out!
              </Typography>

              {showAttemptsLeft && (
                <Typography size="size12" weight={700} className={styles.attemptsLeft}>
                  {attemptsLeftValue}{" "}
                  {attemptsLeftValue === 1 ? "attempt" : "attempts"} left
                </Typography>
              )}

              <div className={styles.actions}>
                <AppButton variant="secondary" className={styles.secondaryButton}>
                  Contact us
                </AppButton>

                {canRetry ? (
                  <AppButton className={styles.primaryButton} onClick={handleTryAgain}>
                    Try Again
                  </AppButton>
                ) : (
                  <AppButton className={styles.primaryButton} onClick={handleSeeOtherOptions}>
                    See other options
                  </AppButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
